import { IconButton } from "@mui/material";
import { Control, Path } from "react-hook-form";

import { IconClose } from "@/assets/icons";
import { FormField } from "@/modules/irohana-components/elements/FormField";
import { Input } from "@/modules/irohana-components/elements/Input";
import { Select } from "@/modules/irohana-components/elements/Select";
import { text } from "@/theme/colors";
import { TOption } from "@/types/common";
import { TProductForm } from "@/api/services/main/organization/product/dtos/product.dto";

/**
 * Thin form-cell wrappers for the editable price tables. Each wraps a form control in a
 * label-less FormField so it reuses the shared Input/Select styling + validation wiring.
 */

type TBaseCell = { control: Control<TProductForm>; name: Path<TProductForm>; disabled?: boolean };

/** Number input with a fixed unit suffix (no clear button), right-aligned error. */
export const NumberCell = ({ control, name, disabled, suffix }: TBaseCell & { suffix: string }) => (
  <FormField control={control} name={name} noLabel isMessageNowrap>
    <Input endAdornment={suffix} clearable={false} disabled={disabled} placeholder="入力してください" />
  </FormField>
);

/** 単価 / money input — always shows the 円 suffix. */
export const MoneyCell = (props: TBaseCell) => <NumberCell {...props} suffix="円" />;

/** Free-text input (ティア / 項目名 / メモ). */
export const TextCell = ({
  control,
  name,
  disabled,
  placeholder = "入力してください",
}: TBaseCell & { placeholder?: string }) => (
  <FormField control={control} name={name} noLabel isMessageNowrap>
    <Input disabled={disabled} placeholder={placeholder} />
  </FormField>
);

/** Master / enum select (国籍 / 分野 / ティア候補). disabled -> read-only + greyed. */
export const SelectCell = ({
  control,
  name,
  options,
  disabled,
  clearable,
  disabledValues,
}: TBaseCell & { options: TOption[]; clearable?: boolean; disabledValues?: string[] }) => (
  <FormField control={control} name={name} noLabel isMessageNowrap>
    <Select options={options} readonly={disabled} clearable={clearable} disabledValues={disabledValues} />
  </FormField>
);

/** Pinned 全ての… default row value — read-only display (nothing to pick / delete). */
export const DefaultLabelCell = ({ label }: { label: string }) => (
  <Input nonControl value={label} disabled clearable={false} fullWidth />
);

/** Row delete (×). */
export const DeleteCell = ({ onClick }: { onClick: () => void }) => (
  <IconButton size="small" onClick={onClick} aria-label="削除" sx={{ color: text.tertiary }}>
    <IconClose />
  </IconButton>
);
